import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AUTH_TOKEN_KEY = "flexspace_auth_token";

export const api = axios.create({
  baseURL: API,
  timeout: 15000,
});

// Attach Bearer token to every outbound request if present in localStorage.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler: wipe the token + bounce to /login so an expired
// session doesn't leave the user staring at a half-loaded dashboard.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/login") {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        window.location.replace("/login");
      }
    }
    return Promise.reject(err);
  }
);

export const endpoints = {
  personas: () => api.get("/personas").then((r) => r.data),
  persona: (id) => api.get(`/personas/${id}`).then((r) => r.data),
  managers: () => api.get("/managers").then((r) => r.data),
  devices: (params) => api.get("/devices", { params }).then((r) => r.data),
  myDevices: (persona) => api.get("/my-devices", { params: { persona } }).then((r) => r.data),
  createOrder: (payload) => api.post("/orders", payload).then((r) => r.data),
  orders: (persona, userEmail) => api.get("/orders", { params: userEmail ? { persona, user_email: userEmail } : (persona ? { persona } : {}) }).then((r) => r.data),
  tickets: (params) => api.get("/tickets", { params }).then((r) => r.data),
  ticket: (id) => api.get(`/tickets/${id}`).then((r) => r.data),
  addComment: (id, payload) => api.post(`/tickets/${id}/comment`, payload).then((r) => r.data),
  updateStatus: (id, payload) => api.post(`/tickets/${id}/status`, payload).then((r) => r.data),
  reports: (persona, userEmail) => api.get("/reports/summary", { params: userEmail ? { persona, user_email: userEmail } : (persona ? { persona } : {}) }).then((r) => r.data),
  approvals: (managerEmail, status) => api.get("/approvals", { params: { manager_email: managerEmail, status } }).then((r) => r.data),
  decideApproval: (orderId, payload) => api.post(`/approvals/${orderId}/decide`, payload).then((r) => r.data),
  resolveToken: (token) => api.get(`/approvals/token/${token}`).then((r) => r.data),
  decideToken: (token, payload) => api.post(`/approvals/token/${token}/decide`, payload).then((r) => r.data),
  advanceTime: () => api.post("/approvals/advance-time").then((r) => r.data),
  createIncident: (payload) => api.post("/incidents", payload).then((r) => r.data),
  servicenowPayload: (ticketId) => api.get(`/tickets/${ticketId}/servicenow-payload`).then((r) => r.data),
  itsmLifecycle: (ticketId) => api.get(`/tickets/${ticketId}/itsm-lifecycle`).then((r) => r.data),
  notifications: (persona) => api.get("/notifications", { params: { persona } }).then((r) => r.data),
  askAssistant: (query, persona) => api.post("/assistant/ask", { query, persona_id: persona }).then((r) => r.data),
  assistantChat: (payload) => api.post("/assistant/chat", payload).then((r) => r.data),
  dexDevices: (persona) => api.get("/dex/devices", { params: { persona } }).then((r) => r.data),
  dexDevice: (assetTag) => api.get(`/dex/devices/${assetTag}`).then((r) => r.data),
  selfHeal: (payload) => api.post("/dex/self-heal", payload).then((r) => r.data),
  fleetHealth: (persona) => api.get("/dex/fleet-health", { params: persona ? { persona } : {} }).then((r) => r.data),
  refreshRadar: (persona) => api.get("/refresh/radar", { params: { persona } }).then((r) => r.data),
  buybackQuote: (payload) => api.post("/buyback/quote", payload).then((r) => r.data),
  finance: (persona) => api.get("/finance/dashboard", { params: persona ? { persona } : {} }).then((r) => r.data),
  aribaPayload: (orderId) => api.get(`/finance/ariba-payload/${orderId}`).then((r) => r.data),
  trackVisit: (payload) => api.post("/track/visit", payload).then((r) => r.data),
  analyticsVisits: (limit = 100) => api.get("/analytics/visits", { params: { limit } }).then((r) => r.data),
  marketplaceApps: (params = {}) => api.get("/marketplace/apps", { params }).then((r) => r.data),
  marketplaceApp: (id) => api.get(`/marketplace/apps/${id}`).then((r) => r.data),
  marketplaceModels: (params = {}) => api.get("/marketplace/models", { params }).then((r) => r.data),
  requestAppInstall: (id, payload) => api.post(`/marketplace/apps/${id}/request-install`, payload).then((r) => r.data),
  requestModelDeployment: (id, payload) => api.post(`/marketplace/models/${id}/request-deployment`, payload).then((r) => r.data),
  aipcDashboard: (persona) => api.get("/aipc/dashboard", { params: persona ? { persona } : {} }).then((r) => r.data),
  myAipc: (persona) => api.get("/aipc/my", { params: { persona } }).then((r) => r.data),
  myReports: (persona, userEmail) => api.get("/reports/my", { params: userEmail ? { persona, user_email: userEmail } : { persona } }).then((r) => r.data),
  relevance: (deviceId, persona) => api.get("/catalogue/relevance", { params: { device_id: deviceId, persona } }).then((r) => r.data),
  relevanceBulk: (persona, category) => api.get("/catalogue/relevance/bulk", { params: category ? { persona, category } : { persona } }).then((r) => r.data),
  marketplaceResources: (params = {}) => api.get("/marketplace/resources", { params }).then((r) => r.data),
  marketplaceSubmissions: (params = {}) => api.get("/marketplace/submissions", { params }).then((r) => r.data),
  createSubmission: (payload) => api.post("/marketplace/submissions", payload).then((r) => r.data),
  decideSubmission: (id, payload) => api.patch(`/marketplace/submissions/${id}/decide`, payload).then((r) => r.data),
  snStatus: () => api.get("/servicenow/status").then((r) => r.data),
  snRecord: (table, sysId) => api.get(`/servicenow/record/${table}/${sysId}`).then((r) => r.data),
  // Lifecycle Intelligence
  lifecycleOverview: () => api.get("/lifecycle/overview").then((r) => r.data),
  lifecycleAgent: (stage) => api.get(`/lifecycle/agents/${stage}`).then((r) => r.data),
  lifecycleAsk: (payload) => api.post("/lifecycle/agents/ask", payload).then((r) => r.data),
  lifecycleRecoverySummary: () => api.get("/lifecycle/recovery/summary").then((r) => r.data),
  lifecycleRecoveryActive: () => api.get("/lifecycle/recovery/active").then((r) => r.data),
  lifecycleWarehouse: () => api.get("/lifecycle/warehouse/summary").then((r) => r.data),
  lifecycleOrchestrator: (payload = {}) => api.post("/lifecycle/orchestrator/run", payload).then((r) => r.data),
  lifecycleCreateRefreshBatch: (payload) => api.post("/lifecycle/actions/create-refresh-batch", payload).then((r) => r.data),
  lifecycleOpenSnWorkflow: (payload) => api.post("/lifecycle/actions/open-sn-workflow", payload).then((r) => r.data),
  lifecycleStory: () => api.get("/lifecycle/story").then((r) => r.data),
  onboardingUser: (params) => api.get("/lifecycle/onboarding/user", { params }).then((r) => r.data),
  onboardingAsk: (payload) => api.post("/lifecycle/onboarding/ask", payload).then((r) => r.data),
  onboardingAdmin: (region = "emea") => api.get("/lifecycle/onboarding/admin", { params: { region } }).then((r) => r.data),
  connectorHealth: () => api.get("/integrations/connectors").then((r) => r.data),
  lifecycleDocsList: () => api.get("/lifecycle/docs/list").then((r) => r.data),
  lifecycleDoc: (id) => api.get(`/lifecycle/docs/${id}`).then((r) => r.data),
  lifecycleAttention: (region = "emea") => api.get("/lifecycle/attention", { params: { region } }).then((r) => r.data),
  lifecycleDexCohort: (filter, region = "emea") => api.get("/lifecycle/dex-cohort", { params: filter ? { filter, region } : { region } }).then((r) => r.data),
  lifecycleDexSelfHeal: (payload) => api.post("/lifecycle/actions/dex-self-heal", payload).then((r) => r.data),
  lifecycleDexProactiveSwap: (payload) => api.post("/lifecycle/actions/dex-proactive-swap", payload).then((r) => r.data),
  lifecycleResolveJoinerBlocker: (payload) => api.post("/lifecycle/actions/resolve-joiner-blocker", payload).then((r) => r.data),
  // Probabilistic forecasting
  probForecast: (agent, sliders = {}) => api.get(`/lifecycle/probabilistic/${agent}`, { params: sliders }).then((r) => r.data),
  probNarrative: (payload) => api.post("/lifecycle/probabilistic/narrative", payload).then((r) => r.data),
  probExcelPreview: () => api.get("/lifecycle/probabilistic/excel/preview").then((r) => r.data),
  probExcelCounts: () => api.get("/lifecycle/probabilistic/excel/counts").then((r) => r.data),
  probFunnelDrill: (stage, limit = 20) => api.get(`/lifecycle/probabilistic/funnel-drill/${stage}`, { params: { limit } }).then((r) => r.data),
  probDataSources: () => api.get("/lifecycle/probabilistic/data-sources").then((r) => r.data),
  agentInputs: (stage) => api.get(`/lifecycle/agent-inputs/${stage}`).then((r) => r.data),
  decisionSample: (stage, termIndex, limit = 50) => api.get(`/lifecycle/decision-sample/${stage}/${termIndex}`, { params: { limit } }).then((r) => r.data),
  handoffPayload: (stage) => api.get(`/lifecycle/handoff-payload/${stage}`).then((r) => r.data),

  // Cockpit — daily-ops surface
  cockpitDailyOps:         (region = "emea") => api.get("/lifecycle/cockpit/daily-ops", { params: { region } }).then((r) => r.data),
  cockpitWarrantyExpiring: (region = "emea") => api.get("/lifecycle/cockpit/warranty-expiring", { params: { region } }).then((r) => r.data),
  cockpitShelfLife:        (limit = 50, region = "emea") => api.get("/lifecycle/cockpit/shelf-life", { params: { limit, region } }).then((r) => r.data),
  cockpitDarkAssets:       (limit = 50, region = "emea") => api.get("/lifecycle/cockpit/dark-assets", { params: { limit, region } }).then((r) => r.data),
  cockpitInvoicesOnHold:   (region = "emea") => api.get("/lifecycle/cockpit/invoices-on-hold", { params: { region } }).then((r) => r.data),
  cockpitOpenTickets:      (groupBy = "operating_line", region = "emea") => api.get("/lifecycle/cockpit/open-tickets", { params: { group_by: groupBy, region } }).then((r) => r.data),
  cockpitOpenTicketsDetail:(groupId, region = "emea") => api.get(`/lifecycle/cockpit/open-tickets/${groupId}`, { params: { region } }).then((r) => r.data),
  cockpitPeriods:          (region = "emea") => api.get("/lifecycle/cockpit/periods", { params: { region } }).then((r) => r.data),
  cockpitPeriodCohort:     (periodId, region = "emea") => api.get(`/lifecycle/cockpit/periods/${periodId}`, { params: { region } }).then((r) => r.data),
  cockpitPushToDemand:     (ticketIds)       => api.post("/lifecycle/cockpit/push-to-demand", { ticket_ids: ticketIds }).then((r) => r.data),
  demandAdhocIntake:       ()                => api.get("/lifecycle/demand/adhoc-intake").then((r) => r.data),
  // Auth
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};
